import { useState, useEffect, useContext } from "react";
import HeaderBar from "./components/headerBar";
import NavTabs from "./components/navTabs";
import {
  CreditCard,
  History,
  ShoppingCart,
  Trash2,
  ChevronRight,
  ShieldCheck,
  Download,
  AlertCircle,
} from "lucide-react";
import Button from "../../components/common/Button";
import axios from "axios";
import PaystackPop from "@paystack/inline-js";
import { useCart } from "../../hooks/useCart";
import { useToast } from "../../hooks/otherHooks/useToast";
import { useAuthState } from "react-redux-django-auth";
import StripeCheckoutButton from "../../utils/stripePayment/CartCheckoutButton";

const StudentPayments = () => {
  const [activeTab, setActiveTab] = useState("cart");
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);

  const {
    cart: cartItems,
    totalCost: subtotal,
    removeFromCart,
    fetchCart,
  } = useCart();
  const { addToast } = useToast();
  const { user: rawUser } = useAuthState();
  const currentUser = (rawUser as any)?.user || (rawUser as any)?.data?.user || rawUser;
  const userEmail = currentUser?.email || "";

  const tabs = [
    { id: "cart", label: "My Cart" },
    { id: "history", label: "History" },
  ];

  const loadPaymentHistory = async () => {
    try {
      setIsLoadingHistory(true);
      const baseUrl = process.env.REACT_APP_API_URL || "";
      const token = localStorage.getItem("access");
      if (!token) return;
      const headers = { Authorization: `JWT ${token}` };

      const response = await axios.get(`${baseUrl}/course/bk-payment-history`, {
        headers,
      });
      if (response.data) {
        setPaymentHistory(response.data);
      }
    } catch (error) {
      console.error("Error loading payment history:", error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  useEffect(() => {
    loadPaymentHistory();
  }, []);

  const handleCheckout = async () => {
    if (!userEmail) {
      addToast({
        message:
          "User account email not loaded. Please ensure you are logged in.",
        bg: "danger",
      });
      return;
    }

    if (cartItems.length === 0) {
      addToast({
        message: "Your shopping cart is empty.",
        bg: "warning",
      });
      return;
    }

    setIsCheckingOut(true);
    const baseUrl = process.env.REACT_APP_API_URL || "";
    const token = localStorage.getItem("access");
    if (!token) {
      setIsCheckingOut(false);
      return;
    }
    const headers = { Authorization: `JWT ${token}` };

    const cartData = cartItems.map((c) => ({
      id: c.item?.id || c.course?.id || c.coursetrack?.id,
      type: c.type,
      price: String(c.price),
    }));

    try {
      // 1. Initiate checkout transaction in Django
      const initiateResponse = await axios.post(
        `${baseUrl}/course/initiate-payment/`,
        {
          email: userEmail,
          amount: subtotal,
          cart: cartData,
        },
        { headers },
      );

      const paystackData = initiateResponse.data;
      if (!paystackData.reference) {
        throw new Error("Invalid checkout response from payment server.");
      }

      // 2. Load Paystack secure inline window
      const publicKey = process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;
      const popup = new PaystackPop();

      popup.newTransaction({
        key: publicKey,
        email: userEmail,
        amount: Math.round(subtotal * 100), // Paystack requires minor units (kobo)
        reference: paystackData.reference,
        onSuccess: async (response) => {
          setIsCheckingOut(false);
          setIsVerifyingPayment(true);
          addToast({
            message: "Authorization successful! Securing payment details...",
            bg: "success",
          });

          // 3. Confirm payment with database
          try {
            const verifyResponse = await axios.post(
              `${baseUrl}/course/verify-payment/`,
              { reference: response.reference },
              { headers },
            );

            if (verifyResponse.data.status === "success") {
              addToast({
                message:
                  "Transaction verified successfully! Welcome to your learning assets.",
                bg: "success",
              });

              // Force-clear local cart and reload database history
              if (fetchCart) await fetchCart();
              await loadPaymentHistory();
            } else {
              addToast({
                message:
                  "Checkout verification failed. Please contact billing support.",
                bg: "danger",
              });
            }
          } catch (verifyError) {
            console.error("Payment confirmation failed:", verifyError);
            addToast({
              message:
                "Network exception during verification. Verifying background queue...",
              bg: "warning",
            });
            // Fallback timeout to let webhook update the records
            setTimeout(async () => {
              if (fetchCart) await fetchCart();
              await loadPaymentHistory();
            }, 3500);
          } finally {
            setIsVerifyingPayment(false);
          }
        },
        onCancel: () => {
          addToast({
            message: "Payment process aborted by user.",
            bg: "danger",
          });
          setIsCheckingOut(false);
        },
        onError: (err) => {
          console.error("Paystack popup error:", err);
          addToast({
            message:
              "A gateway error occurred. Please verify details and try again.",
            bg: "danger",
          });
          setIsCheckingOut(false);
        },
      });
    } catch (error) {
      console.error("Checkout initiation exception:", error);
      addToast({
        message:
          error.response?.data?.error ||
          "Unable to contact secure billing server. Please try again.",
        bg: "danger",
      });
      setIsCheckingOut(false);
    }
  };

  const handleDownloadReceipts = async () => {
    setDownloadingReport(true);
    const apiURL = process.env.REACT_APP_API_URL || "";
    const accessToken = localStorage.getItem("access");

    if (!accessToken) {
      addToast({
        message: "Please log in to export transactions.",
        bg: "warning",
      });
      setDownloadingReport(false);
      return;
    }

    try {
      const response = await axios.get(`${apiURL}/course/download-receipts/`, {
        headers: {
          Authorization: `JWT ${accessToken}`,
          Accept: "*/*",
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `paystack_receipts_${userEmail}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      addToast({
        message: "PDF Receipt compilation downloaded successfully.",
        bg: "success",
      });
    } catch (error) {
      console.error("Export receipts failed:", error);
      addToast({
        message: "No successful transactions found to compile in Paystack.",
        bg: "danger",
      });
    } finally {
      setDownloadingReport(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const d = new Date(dateString);
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  };

  const getDescription = (payment) => {
    if (payment.courses) {
      return `${payment.courses.title} (Course)`;
    }
    if (payment.coursetracks) {
      return `${payment.coursetracks.title} (Track)`;
    }
    return "Course Learning Package";
  };

  const getStatusPill = (status) => {
    let bg = "cht-bg-yellow-50 cht-text-yellow-600 cht-border-yellow-100";
    let label = "Pending";
    if (status === "paid") {
      bg = "cht-bg-green-50 cht-text-green-600 cht-border-green-100";
      label = "Successful";
    } else if (status === "failed") {
      bg = "cht-bg-red-50 cht-text-red-600 cht-border-red-100";
      label = "Failed";
    }
    return (
      <span
        className={`cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest cht-px-3 cht-py-1.5 cht-rounded-full cht-border ${bg}`}
      >
        {label}
      </span>
    );
  };

  return (
    <section className="cht-animate-in cht-fade-in cht-duration-700">
      <HeaderBar pageHeader="Payment & Billing">
        <NavTabs tabs={tabs} activeTab={activeTab} onTabSelect={setActiveTab} />
      </HeaderBar>

      <main className="cht-container cht-space-y-10">
        <div className="cht-grid cht-grid-cols-1 lg:cht-grid-cols-12 cht-gap-8 cht-max-w-6xl cht-mx-auto">
          {/* Main Content Area (8 cols) */}
          <div className="lg:cht-col-span-8 cht-space-y-8">
            {activeTab === "cart" ? (
              <div className="cht-bg-white cht-rounded-[32px] cht-overflow-hidden cht-shadow-[0_10px_40px_rgba(0,0,0,0.02)] cht-border cht-border-gray-50">
                <div className="cht-p-6 cht-border-b cht-border-gray-50 cht-flex cht-items-center cht-justify-between">
                  <h3 className="cht-text-lg cht-font-bold cht-text-black">
                    Shopping Cart
                  </h3>
                  <span className="cht-text-xs cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    {cartItems.length} Items
                  </span>
                </div>

                {cartItems.length > 0 ? (
                  <div className="cht-divide-y cht-divide-gray-50">
                    {cartItems.map((item) => {
                      const itemTitle =
                        item.item?.title ||
                        item.course?.title ||
                        item.coursetrack?.title ||
                        "Course Package";
                      const itemPrice = parseFloat(item.price) || 0;
                      return (
                        <div
                          key={item.id}
                          className="cht-p-6 cht-flex cht-items-center cht-justify-between cht-group hover:cht-bg-gray-50/50 cht-transition-colors"
                        >
                          <div className="cht-flex cht-items-center cht-gap-4">
                            <div className="cht-w-12 cht-h-12 cht-bg-[#3e57da]/10 cht-rounded-[12px] cht-flex cht-items-center cht-justify-center text-[#3e57da]">
                              <ShoppingCart size={20} />
                            </div>
                            <div>
                              <h4 className="cht-text-sm cht-font-bold cht-text-black">
                                {itemTitle}
                              </h4>
                              <p className="cht-text-[10px] cht-text-[#3e57da] cht-font-bold cht-uppercase cht-tracking-wider cht-mt-0.5">
                                {item.presence} learning format
                              </p>
                            </div>
                          </div>
                          <div className="cht-flex cht-items-center cht-gap-6">
                            <span className="cht-text-sm cht-font-bold cht-text-black">
                              ₦{itemPrice.toLocaleString()}
                            </span>
                            <button
                              onClick={() =>
                                removeFromCart(
                                  item.item?.id ||
                                    item.course?.id ||
                                    item.coursetrack?.id,
                                  item.type,
                                  item.presence,
                                  itemTitle,
                                )
                              }
                              className="cht-p-2.5 cht-rounded-[12px] cht-text-gray-300 hover:cht-text-red-500 hover:cht-bg-red-50/50 cht-opacity-0 group-hover:cht-opacity-100 cht-transition-all"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="cht-p-20 cht-text-center cht-space-y-4">
                    <ShoppingCart
                      size={40}
                      className="cht-mx-auto cht-text-gray-200"
                    />
                    <p className="cht-text-sm cht-text-gray-400 font-medium">
                      Your cart is empty.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="cht-bg-white cht-rounded-[32px] cht-overflow-hidden cht-shadow-[0_10px_40px_rgba(0,0,0,0.02)] cht-border cht-border-gray-50">
                <div className="cht-p-6 cht-border-b cht-border-gray-50 cht-flex cht-items-center cht-justify-between">
                  <h3 className="cht-text-lg cht-font-bold cht-text-black">
                    Transaction History
                  </h3>
                  {paymentHistory.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      leftIcon={<Download size={14} />}
                      onClick={handleDownloadReceipts}
                      disabled={downloadingReport}
                    >
                      {downloadingReport ? "Downloading..." : "Export Receipts"}
                    </Button>
                  )}
                </div>
                <div className="cht-overflow-x-auto">
                  <table className="cht-w-full cht-text-left cht-border-collapse">
                    <thead>
                      <tr
                        className="cht-border-b cht-border-gray-100"
                        style={{ height: 48 }}
                      >
                        <th className="cht-px-6 cht-text-xs cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-pb-4">
                          Date
                        </th>
                        <th className="cht-px-6 cht-text-xs cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-pb-4">
                          Description
                        </th>
                        <th className="cht-px-6 cht-text-xs cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-pb-4">
                          Amount
                        </th>
                        <th className="cht-px-6 cht-text-xs cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-pb-4">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoadingHistory ? (
                        [1, 2, 3].map((i) => (
                          <tr
                            key={i}
                            className="cht-border-b cht-border-gray-100 cht-animate-pulse"
                            style={{ height: 56 }}
                          >
                            <td className="cht-px-6">
                              <div className="cht-h-3.5 cht-bg-gray-200/80 cht-rounded-md cht-w-16"></div>
                            </td>
                            <td className="cht-px-6">
                              <div className="cht-h-3.5 cht-bg-gray-200/80 cht-rounded-md cht-w-48"></div>
                            </td>
                            <td className="cht-px-6">
                              <div className="cht-h-3.5 cht-bg-gray-200/80 cht-rounded-md cht-w-20"></div>
                            </td>
                            <td className="cht-px-6">
                              <div className="cht-h-6 cht-bg-gray-200/80 cht-rounded-full cht-w-24"></div>
                            </td>
                          </tr>
                        ))
                      ) : paymentHistory.length > 0 ? (
                        paymentHistory.map((item) => (
                          <tr
                            key={item.id}
                            className="cht-border-b cht-border-gray-100 hover:cht-bg-gray-50/50 cht-transition-all"
                            style={{ height: 56 }}
                          >
                            <td className="cht-px-6 cht-text-xs cht-font-bold cht-text-gray-500">
                              {formatDate(item.created_at)}
                            </td>
                            <td className="cht-px-6">
                              <p className="cht-text-sm cht-font-bold cht-text-black">
                                {getDescription(item)}
                              </p>
                            </td>
                            <td className="cht-px-6 cht-text-sm cht-font-bold cht-text-black">
                              ₦{parseFloat(item.amount).toLocaleString()}
                            </td>
                            <td className="cht-px-6">
                              {getStatusPill(item.status)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={4}
                            className="cht-px-6 cht-py-16 cht-text-center"
                          >
                            <CreditCard
                              size={32}
                              className="cht-mx-auto cht-text-gray-200 cht-mb-3"
                            />
                            <div className="cht-text-sm cht-text-gray-400 cht-font-bold cht-uppercase cht-tracking-widest">
                              No payment records found
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar Area (4 cols) */}
          <div className="lg:cht-col-span-4">
            <div className="cht-sticky cht-top-24 cht-bg-[#1e293b] cht-text-white cht-rounded-[32px] cht-p-8 cht-shadow-2xl cht-space-y-8">
              <h3 className="cht-text-xl cht-font-bold">Order Summary</h3>

              <div className="cht-space-y-4">
                <div className="cht-flex cht-justify-between cht-text-sm cht-font-semibold cht-text-gray-400">
                  <span>Subtotal</span>
                  <span>₦{subtotal.toLocaleString()}</span>
                </div>
                <div className="cht-flex cht-justify-between cht-text-sm cht-font-semibold cht-text-gray-400">
                  <span>VAT (0%)</span>
                  <span>₦0</span>
                </div>
                <div className="cht-h-px cht-bg-white/10 cht-my-4"></div>
                <div className="cht-flex cht-justify-between cht-items-end">
                  <div>
                    <p className="cht-text-xs cht-font-bold cht-text-gray-400 cht-uppercase cht-tracking-widest">
                      Total Amount
                    </p>
                    <p className="cht-text-3xl cht-font-black">
                      ₦{subtotal.toLocaleString()}
                    </p>
                  </div>
                  <div className="cht-flex cht-items-center cht-gap-1.5 cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest cht-text-green-400 cht-bg-green-400/10 cht-border cht-border-green-400/20 cht-px-3.5 cht-py-2 cht-rounded-full">
                    <ShieldCheck size={12} /> SECURE
                  </div>
                </div>
              </div>

              {/* <Button 
                variant="primary" 
                size="lg" 
                className="cht-w-full" 
                rightIcon={<ChevronRight size={18} />}
                onClick={handleCheckout}
                disabled={isCheckingOut || cartItems.length === 0}
              >
                {isCheckingOut ? "Processing..." : "Checkout with SecurePay"}
              </Button> */}

              <StripeCheckoutButton />

              <div className="cht-flex cht-items-center cht-justify-center cht-gap-6 cht-opacity-40">
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/5/5e/Visa_Inc._logo.svg"
                  className="cht-h-3"
                  alt="visa"
                />
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg"
                  className="cht-h-5"
                  alt="mastercard"
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Glassmorphism Verification Background Overlay */}
      {isVerifyingPayment && (
        <div className="cht-fixed cht-inset-0 cht-z-[3000] cht-flex cht-items-center cht-justify-center cht-bg-black/40 cht-backdrop-blur-md cht-animate-in cht-fade-in cht-duration-300">
          <div className="cht-bg-white/95 cht-backdrop-blur-2xl cht-p-8 cht-rounded-[24px] cht-shadow-2xl cht-border cht-border-white/40 cht-flex cht-flex-col cht-items-center cht-space-y-5 cht-max-w-sm cht-text-center cht-animate-in cht-zoom-in-95">
            <div className="cht-w-12 cht-h-12 cht-border-[3px] cht-border-[#3e57da] cht-border-t-transparent cht-rounded-full cht-animate-spin"></div>
            <div>
              <h4 className="cht-text-base cht-font-black cht-text-black">
                Confirming Purchase
              </h4>
              <p className="cht-text-xs cht-text-gray-500 cht-mt-1.5 cht-font-medium leading-relaxed">
                Verifying your payment credentials with the secure gateway.
                Please do not close this window.
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default StudentPayments;
